import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Search, Loader2, Users, MoreHorizontal, Pencil, Trash2, Upload, Check, X, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline editable cell
function EditableCell({ value, field, clientId, type = 'text', onSave, isCurrency }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    const newVal = type === 'number' ? (draft === '' ? null : parseFloat(draft)) : (draft || null);
    const oldVal = type === 'number' ? (value ?? null) : (value || null);
    if (newVal !== oldVal) {
      onSave(clientId, field, newVal);
    }
    setEditing(false);
  };

  const cancel = () => { setDraft(value ?? ''); setEditing(false); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          type={type}
          step={type === 'number' ? '0.01' : undefined}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={save}
          className="h-7 text-sm px-2 py-0 min-w-[60px]"
          data-testid={`inline-edit-${field}-${clientId}`}
        />
      </div>
    );
  }

  const display = isCurrency && value != null ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : (value || '-');

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1.5 py-0.5 rounded transition-colors inline-block min-w-[30px]"
      title="Click to edit"
      data-testid={`cell-${field}-${clientId}`}
    >
      {display}
    </span>
  );
}

export default function ClientsPage() {
  const { currentCompany } = useCompany();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | paid | unpaid
  const [sortBy, setSortBy] = useState('name'); // name | total_paid_desc | payment_count_desc | balance_due_desc
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', store: '', current_amount: '', terms: '', balance_due: '', start_date: '', payment_method: '', description: '', notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadClientId, setUploadClientId] = useState(null);

  const pageLabel = currentCompany?.slug === 'veriquant' ? 'Contact List' : currentCompany?.slug === 'saddlebrook' ? 'Store Owners' : 'Contact List';
  const singularLabel = currentCompany?.slug === 'saddlebrook' ? 'Store Owner' : 'Contact';
  const isSaddlebrook = currentCompany?.slug === 'saddlebrook';

  const displayedClients = useMemo(() => {
    let list = [...clients];
    if (isSaddlebrook) {
      if (paymentFilter === 'paid') list = list.filter(c => (c.payment_count || 0) > 0);
      else if (paymentFilter === 'unpaid') list = list.filter(c => (c.payment_count || 0) === 0);
      if (sortBy === 'total_paid_desc') list.sort((a, b) => (b.total_paid || 0) - (a.total_paid || 0));
      else if (sortBy === 'payment_count_desc') list.sort((a, b) => (b.payment_count || 0) - (a.payment_count || 0));
      else if (sortBy === 'balance_due_desc') list.sort((a, b) => (b.balance_due || 0) - (a.balance_due || 0));
      else list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return list;
  }, [clients, paymentFilter, sortBy, isSaddlebrook]);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/clients/export/csv',
    importUrl: '/api/clients/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchClients(),
    entityName: 'clients'
  });

  useEffect(() => {
    if (currentCompany) fetchClients();
  }, [currentCompany, search]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = { company_id: currentCompany.id };
      if (search) params.search = search;
      const response = await axios.get(`${API_URL}/api/clients`, { params, withCredentials: true });
      setClients(response.data);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };

  // Inline save handler - update a single field
  const handleInlineSave = async (clientId, field, value) => {
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}`, { [field]: value }, { withCredentials: true });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, [field]: value } : c));
    } catch { toast.error('Failed to save'); fetchClients(); }
  };

  const handleAddClient = async () => {
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setIsSubmitting(true);
    try {
      const payload = {
        company_id: currentCompany.id,
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        store: formData.store || null,
        description: formData.description || null,
        current_amount: formData.current_amount ? parseFloat(formData.current_amount) : null,
        terms: formData.terms || null,
        balance_due: formData.balance_due ? parseFloat(formData.balance_due) : null,
        start_date: formData.start_date || null,
        payment_method: formData.payment_method || null,
        notes: formData.notes || null
      };
      await axios.post(`${API_URL}/api/clients`, payload, { withCredentials: true });
      toast.success(`${singularLabel} added`);
      setIsAddDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', store: '', current_amount: '', terms: '', balance_due: '', start_date: '', payment_method: '', description: '', notes: '' });
      fetchClients();
    } catch { toast.error('Failed to add'); }
    finally { setIsSubmitting(false); }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      store: client.store || '',
      current_amount: client.current_amount != null ? String(client.current_amount) : '',
      terms: client.terms || '',
      balance_due: client.balance_due != null ? String(client.balance_due) : '',
      start_date: client.start_date || '',
      payment_method: client.payment_method || '',
      description: client.description || '',
      notes: client.notes || '',
    });
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        store: formData.store || null,
        description: formData.description || null,
        current_amount: formData.current_amount === '' ? null : parseFloat(formData.current_amount),
        terms: formData.terms || null,
        balance_due: formData.balance_due === '' ? null : parseFloat(formData.balance_due),
        start_date: formData.start_date || null,
        payment_method: formData.payment_method || null,
        notes: formData.notes || null,
      };
      await axios.put(`${API_URL}/api/clients/${editingClient.id}`, payload, { withCredentials: true });
      toast.success(`${singularLabel} updated`);
      setEditingClient(null);
      fetchClients();
    } catch { toast.error('Failed to update'); }
    finally { setIsSubmitting(false); }
  };

  const handleDeleteClient = async () => {
    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/clients/${selectedClient.id}`, { withCredentials: true });
      toast.success(`${singularLabel} deleted`);
      setIsDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch { toast.error('Failed to delete'); }
    finally { setIsSubmitting(false); }
  };

  const triggerUpload = (clientId) => {
    setUploadClientId(clientId);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadClientId) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setUploading(uploadClientId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/files/upload?company_id=${currentCompany.id}&client_id=${uploadClientId}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); setUploadClientId(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  if (!currentCompany) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="client-row-file-input" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{pageLabel}</h1>
          <p className="text-gray-500 mt-1">
            {isSaddlebrook ? 'Click name to view details, or any other cell to edit inline' : `Manage your ${pageLabel.toLowerCase()}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="clients" />
          <Button onClick={() => { setFormData({ name: '', email: '', phone: '', store: '', current_amount: '', terms: '', balance_due: '', start_date: '', payment_method: '', description: '', notes: '' }); setIsAddDialogOpen(true); }} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />Add {singularLabel}
          </Button>
        </div>
      </div>

      {/* Search + Filter + Sort */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder={`Search ${pageLabel.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-clients" />
        </div>
        {isSaddlebrook && (
          <>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="payment-filter"
            >
              <option value="all">All owners</option>
              <option value="paid">Paid (has payments)</option>
              <option value="unpaid">Unpaid (no payments)</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="sort-clients"
            >
              <option value="name">Sort: Name (A→Z)</option>
              <option value="total_paid_desc">Sort: Total Paid (high→low)</option>
              <option value="payment_count_desc">Sort: Payment Count (high→low)</option>
              <option value="balance_due_desc">Sort: Balance Due (high→low)</option>
            </select>
          </>
        )}
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : displayedClients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No {pageLabel.toLowerCase()} match</h3>
          <p className="text-gray-500 mb-4">Try changing the filter or search</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Terms</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayedClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50/50" data-testid={`client-row-${client.id}`}>
                    <td className="px-4 py-2.5 font-medium text-sm">
                      <button
                        type="button"
                        onClick={() => openEditClient(client)}
                        className="text-blue-700 hover:text-blue-900 hover:underline text-left"
                        data-testid={`client-name-link-${client.id}`}
                        title="Click to edit"
                      >
                        {client.name}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      <EditableCell value={client.store} field="store" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      <EditableCell value={client.description} field="description" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm font-medium text-gray-900">
                      <EditableCell value={client.current_amount} field="current_amount" clientId={client.id} type="number" onSave={handleInlineSave} isCurrency />
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm font-medium">
                      <EditableCell value={client.balance_due} field="balance_due" clientId={client.id} type="number" onSave={handleInlineSave} isCurrency />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      <EditableCell value={client.terms} field="terms" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      <EditableCell value={client.start_date} field="start_date" clientId={client.id} type="date" onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      <EditableCell value={client.payment_method} field="payment_method" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      <EditableCell value={client.email} field="email" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">
                      <EditableCell value={client.phone} field="phone" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500 max-w-[150px]">
                      <EditableCell value={client.notes} field="notes" clientId={client.id} onSave={handleInlineSave} />
                    </td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditClient(client)} data-testid={`edit-client-${client.id}`}>
                            <Pencil className="w-4 h-4 mr-2" />Edit Info
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => triggerUpload(client.id)} data-testid={`upload-file-client-${client.id}`}>
                            <Upload className="w-4 h-4 mr-2" />{uploading === client.id ? 'Uploading...' : 'Upload File'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)} data-testid={`view-details-client-${client.id}`}>
                            <FolderOpen className="w-4 h-4 mr-2" />View Payments & Files
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedClient(client); setIsDeleteDialogOpen(true); }} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-sm text-gray-500">{clients.length} {clients.length === 1 ? singularLabel.toLowerCase() : pageLabel.toLowerCase()}</div>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="add-client-dialog">
          <DialogHeader><DialogTitle>Add New {singularLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Client name" data-testid="client-name-input" /></div>
              <div className="space-y-2"><Label>Store</Label><Input value={formData.store} onChange={(e) => setFormData({ ...formData, store: e.target.value })} placeholder="Store name" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g., Repurchase" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Amount ($)</Label><Input type="number" step="0.01" value={formData.current_amount} onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })} placeholder="0.00" /></div>
              <div className="space-y-2"><Label>Balance Due ($)</Label><Input type="number" step="0.01" value={formData.balance_due} onChange={(e) => setFormData({ ...formData, balance_due: e.target.value })} placeholder="0.00" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Terms</Label><Input value={formData.terms} onChange={(e) => setFormData({ ...formData, terms: e.target.value })} placeholder="e.g. Monthly" /></div>
              <div className="space-y-2"><Label>Payment Method</Label><Input value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} placeholder="e.g. Zelle, Wire" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone number" /></div>
            </div>
            <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddClient} disabled={isSubmitting} data-testid="save-client-btn">{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${singularLabel}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingClient} onOpenChange={(o) => !o && setEditingClient(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle>Edit {singularLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="edit-client-name-input" /></div>
              <div className="space-y-2"><Label>Store</Label><Input value={formData.store} onChange={(e) => setFormData({ ...formData, store: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Amount ($)</Label><Input type="number" step="0.01" value={formData.current_amount} onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })} /></div>
              <div className="space-y-2"><Label>Balance Due ($)</Label><Input type="number" step="0.01" value={formData.balance_due} onChange={(e) => setFormData({ ...formData, balance_due: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Terms</Label><Input value={formData.terms} onChange={(e) => setFormData({ ...formData, terms: e.target.value })} /></div>
              <div className="space-y-2"><Label>Payment Method</Label><Input value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} /></div>
            {isSaddlebrook && (
              <div className="pt-2 border-t border-gray-100">
                <Button variant="outline" size="sm" onClick={() => { const id = editingClient.id; setEditingClient(null); navigate(`/clients/${id}`); }} data-testid="edit-dialog-view-details">
                  <FolderOpen className="w-4 h-4 mr-2" />View Payments & Files
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingClient(null)}>Cancel</Button>
            <Button onClick={handleUpdateClient} disabled={isSubmitting} data-testid="save-edit-client-btn">{isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {singularLabel}</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{selectedClient?.name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
